import {useEffect, useState} from 'react'
import {useParams} from 'react-router'
import SkeletonCards from '@/customComponents/editRideComponents/SkeletonCards.jsx'
import FlightDelayedPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/FlightDelayedPopup.jsx'
import LostBaggagePopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/LostBaggagePopup.jsx'
import AirportPickupPerksPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/AirportPickupPerksPopup.jsx'
import ComingSoonPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/ComingSoonPopup.jsx'
import PayAfterDropoffPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PayAfterDropoffPopup.jsx'
import PoliciesPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PoliciesPopup.jsx'
import RouteCard from '@/customComponents/editRideComponents/RouteCard.jsx'
import DateTimeCard from '@/customComponents/editRideComponents/DateTimeCard.jsx'
import {PassengersCard, ChildSeatsCard, LuggageItemsCard, OversizedCard} from '@/customComponents/editRideComponents/SummaryCards.jsx'
import CarDetailsCard from '@/customComponents/editRideComponents/CarDetailsCard.jsx'
import SpecialRequestCard from '@/customComponents/editRideComponents/SpecialRequestCard.jsx'
import PaymentMethodsCard from '@/customComponents/editRideComponents/PaymentMethodsCard.jsx'
import InfoButtons from '@/customComponents/editRideComponents/InfoButtons.jsx'
import AlertMessage from '@/customComponents/homeComponents/AlertMessage.jsx'
import ExclusivePerksBar from '@/customComponents/sharedComponents/ExclusivePerksBar.jsx'
import RideBackButton from '@/customComponents/sharedComponents/RideBackButton.jsx'
import AirportPerksModal from '@/customComponents/sharedComponents/AirportPerksModal.jsx'
import useEditTrip from '@/hooks/useEditTrip'

/**
 * Read-only twin of RideEdit / RideConfirm — the "view the trip details" page
 * (/rides/:rideId/view), reached from the right-arrow button on the trip-info bar
 * of the ride details page.
 *
 * Deliberately the SAME cards in the SAME order as the edit page, each in
 * `readOnly` mode: every pencil, picker, dropdown and the submit button are gone,
 * so nothing here can change the trip. It reuses useEditTrip purely as the data
 * source — the hook only persists via doUpdate/doConfirm, which this page never
 * calls — so the view can't drift from what the edit page shows.
 *
 * The informational popups (perks, info pills, policies) stay: they read, they
 * don't write. Editing and cancelling live on the ride details page.
 */
export default function RideTripView() {
    const {rideId} = useParams()
    // defaultFirstCard: false — this page only DISPLAYS the booking, so it must not
    // pre-select a card the trip does not name. An unpaid trip says so instead.
    const e = useEditTrip(rideId, {defaultFirstCard: false})
    const isReturnFlow = e.trip?.trip_type === 'ReturnBooking'
    const [showPoliciesPopup, setShowPoliciesPopup] = useState(false)
    const [showPerksModal, setShowPerksModal] = useState(false)

    useEffect(() => {
        document.body.classList.add('ride-edit-page')
        return () => document.body.classList.remove('ride-edit-page')
    }, [])

    if (e.isLoading) {
        return <div className="py-5 pb-10 max-md:bg-(--bg-grey)"><SkeletonCards/></div>
    }

    if (!e.trip) {
        return <div className="py-5 pb-10"><div className="max-w-3xl mx-auto text-center p-4"><p className="text-(--text-secondary)">Trip not found</p></div></div>
    }

    return (
        <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
            {/* Back sits at the left edge of the content column, vertically centred on
                the title block (which stays centred — hence absolute, not a flex row). */}
            <div className="max-w-3xl mx-auto text-center mb-5 relative px-14">
                <RideBackButton className="absolute left-2 top-1/2 -translate-y-1/2"/>
                <h1 className="font-bold text-2xl">{isReturnFlow ? 'Return Booking Details' : 'Booking Details'}</h1>
                <p className="mb-0! text-sm text-(--text-secondary)">Trip {e.trip.reference || `#${rideId}`}</p>
            </div>

            <div className="max-w-3xl mx-auto mb-5 flex flex-col gap-7">
                <div className="prebook-compare">
                    <ExclusivePerksBar onClick={() => setShowPerksModal(true)}/>
                </div>
            </div>

            {/* No isLocked overlay here — a Completed / Cancelled trip is exactly the
                case where the passenger still wants to READ its details. */}
            <div className="relative max-w-3xl mx-auto flex flex-col gap-4 p-5 pb-8 bg-(--bg-grey) rounded-(--radius-2xl) max-md:p-0 max-md:rounded-none max-md:bg-transparent">
                <AlertMessage paymentHold={e.trip.payment_hold}/>

                <RouteCard title={e.isFromAirport ? 'From Airport' : 'Start Ride Address'} summary="origin" showBanner={false} includeStopovers={e.isToAirport}
                           trip={{...e.trip, origin: e.committedDestinations.origin, destination: e.committedDestinations.destination, stop_overs: e.committedDestinations.stopOvers}}
                           expanded={e.expanded.has('route')} onToggle={() => e.toggle('route')} readOnly/>

                <RouteCard title="Drop off Address" summary="destination" showBanner={false} includeStopovers={!e.isToAirport}
                           trip={{...e.trip, origin: e.committedDestinations.origin, destination: e.committedDestinations.destination, stop_overs: e.committedDestinations.stopOvers}}
                           expanded={e.expanded.has('dropoff')} onToggle={() => e.toggle('dropoff')} readOnly/>

                <DateTimeCard
                    trip={e.trip} parentScheduledAt={e.trip?.parent_trip?.scheduled_at}
                    isAirportTrip={e.isAirportTrip}
                    isFromAirport={e.isFromAirport} isToAirport={e.isToAirport}
                    flightNumber={e.flightNumber || e.sharedFlightNumber}
                    readOnly
                />

                <PassengersCard trip={e.trip} expanded={e.expanded.has('passengers')} onToggle={() => e.toggle('passengers')} readOnly/>

                <ChildSeatsCard trip={e.trip} expanded={e.expanded.has('childseats')} onToggle={() => e.toggle('childseats')} readOnly/>

                <LuggageItemsCard trip={e.trip} expanded={e.expanded.has('luggage')} onToggle={() => e.toggle('luggage')} readOnly/>

                <OversizedCard trip={e.trip} expanded={e.expanded.has('oversized')} onToggle={() => e.toggle('oversized')} readOnly/>

                <div className="flex gap-4 items-stretch max-md:flex-col">
                    <SpecialRequestCard trip={e.trip} specialRequest={e.specialRequest} contact={e.contact} readOnly/>
                    <CarDetailsCard trip={e.trip} driverTip={e.driverTip} readOnly/>
                </div>

                <PaymentMethodsCard trip={e.trip} savedCards={e.savedCards} selectedCardId={e.selectedCardId} walletAmountCents={e.trip?.wallet_amount_cents} selectedOnly={true} readOnly/>

                <InfoButtons onInfoPopup={e.setInfoPopup}/>

                {/* Reworded from the edit page's "By proceeding…" — nothing is being
                    submitted here, so it's a plain link to the same policies popup. */}
                <p className="mb-0 text-sm text-center cursor-pointer underline" onClick={() => setShowPoliciesPopup(true)}>Review the terms that apply to this booking.</p>
            </div>

            <FlightDelayedPopup isOpen={e.infoPopup === 'flightDelayed'} onClose={() => e.setInfoPopup(null)}/>
            <LostBaggagePopup isOpen={e.infoPopup === 'lostBaggage'} onClose={() => e.setInfoPopup(null)}/>
            <AirportPickupPerksPopup isOpen={e.infoPopup === 'morePerks'} onClose={() => e.setInfoPopup(null)}/>
            {/* Cancellation, Change & Refund — one merged CMR popup (opens on the
                Cancellation tab; Refund is a tab within it). */}
            <PoliciesPopup isOpen={e.infoPopup === 'cancellation'} onClose={() => e.setInfoPopup(null)} trip={e.trip} initialStep="cancellationPolicies"/>
            <PayAfterDropoffPopup isOpen={e.infoPopup === 'payAfterDropoff'} onClose={() => e.setInfoPopup(null)}/>
            <ComingSoonPopup isOpen={e.infoPopup === 'findDriver'} onClose={() => e.setInfoPopup(null)} title="Find Your Driver"/>
            <PoliciesPopup isOpen={showPoliciesPopup} onClose={() => setShowPoliciesPopup(false)} trip={e.trip}/>

            {/* Exclusive Airport Pickup Perks — perks list + nested perk detail views */}
            <AirportPerksModal isOpen={showPerksModal} onClose={() => setShowPerksModal(false)} trip={e.trip}/>
        </div>
    )
}
